<template>
  <BaseDriverContactSection
    :id="id"
    :driver-contact="driverContact"
    :editable="editable"
    @save="onSaveDriverContact"
  />
</template>

<script lang="ts" setup>
import axios from 'axios';
import type { Ref } from 'vue';
import { ref, toRef, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { useToast } from '@rygen/rygen-ui/composables';
import { DomainReference, Loadable, Optional } from '@rygen/rygen-ui/model';

import api from '@/api';
import { DriverContact } from '@/model';
import { ParentEntity, useParentEntityStore } from '@/stores/ParentEntity';

import BaseDriverContactSection, {
  DriverContactPayload,
} from '@/components/shipment/driverContact/BaseDriverContactSection.vue';

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  editable: {
    type: Boolean,
    default: () => false,
  },
});
const { id, editable } = toRefs(props);

const { t } = useI18n();
const { owningClient, parentEntity } = useParentEntityStore();
const toast = useToast();

const driverContact: Ref<Loadable<DriverContact>> = ref({ loading: false });

const shipmentId: Ref<Optional<number>> = toRef(() => parentEntity.value?.id);

const loadDriverContact = async (shipmentId: number) => {
  try {
    driverContact.value.loading = true;
    driverContact.value.data =
      await api.shipment.loadDriverContactForShipment(shipmentId);
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    if (axios.isAxiosError(e) && e.response?.status === 404) {
      // No driver contact is fine
      return;
    }

    driverContact.value.error = e;
    console.error('Error loading driver contact', e);
    toast.addError({
      detail: t('errors.loadingParam', {
        param: t(t('shipments.driverContact')),
      }),
      error: e,
    });
  } finally {
    driverContact.value.loading = false;
  }
};

const onSaveDriverContact = async ({
  driverContact: newVal,
  onSuccessCallback,
  onErrorCallback,
}: DriverContactPayload) => {
  if (!shipmentId.value) {
    return;
  }

  const isUpdate = !!newVal.id;

  try {
    driverContact.value.loading = true;

    newVal.shipmentId = shipmentId.value;
    driverContact.value.data = isUpdate
      ? await api.shipment.updateDriverContactForShipment(
          shipmentId.value,
          newVal,
        )
      : await api.shipment.createDriverContactForShipment(
          shipmentId.value,
          newVal,
        );

    onSuccessCallback?.();
  } catch (e) {
    onErrorCallback?.(e);
    if (axios.isCancel(e)) {
      return;
    }

    driverContact.value.error = e;
    console.error('Error upserting driver contact', e);
    toast.addError({
      detail: t(isUpdate ? 'errors.updatingParam' : 'errors.savingParam', {
        param: t(t('shipments.driverContact')),
      }),
      error: e,
    });
  } finally {
    driverContact.value.loading = false;
  }
};

watch(
  parentEntity,
  (newVal: Optional<ParentEntity>, oldVal: Optional<ParentEntity>) => {
    const isSameId = newVal?.id === oldVal?.id;
    const isSameOwner = newVal?.owner?.id === oldVal?.owner?.id;
    if (!newVal?.id || (isSameId && !isSameOwner)) {
      return;
    }

    loadDriverContact(newVal.id);
  },
  { immediate: true },
);

watch(
  () => owningClient.value?.id,
  (newVal: DomainReference['id']) => {
    if (!newVal || !shipmentId.value) {
      return;
    }

    loadDriverContact(shipmentId.value);
  },
  { immediate: true },
);
</script>
